import React from 'react';

import { Trans } from '@lingui/macro';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import * as auth0 from '../utils/auth0';
import Paper from '../misc/Paper';

function hasAuthType(auths, type) {
	for (let i = 0; i < auths.length; i++) {
		if (auths[i].type === type) {
			return true;
		}
	}

	return false;
}

// Core's auth list is what normally supplies the Auth0 tenant, but /about and
// /config require a JWT -- so on a page that is not logged in yet the list can
// come back without a usable auth0 entry, and the login page would then issue
// an /authorize request with an empty client_id. Fall back to the tenant baked
// in at build time (see utils/auth0.js). Since there is no tenant picker any
// more, the list is only used for its first entry.
function initAuths(auths) {
	const entries = auths
		.map((a) => {
			const params = a.split(' ');
			if (params[0] !== 'auth0') {
				return null;
			}

			const entry = { type: 'auth0', id: '', domain: '', audience: '', client_id: '' };
			for (let i = 1; i < params.length; i++) {
				const kv = params[i].split('=');
				if (kv.length !== 2) {
					continue;
				}

				switch (kv[0]) {
					case 'domain':
						entry.domain = kv[1];
						break;
					case 'audience':
						entry.audience = kv[1];
						break;
					case 'clientid':
						entry.client_id = kv[1];
						break;
					default:
						break;
				}
			}

			if (entry.domain.length === 0 || entry.audience.length === 0 || entry.client_id.length === 0) {
				return null;
			}

			entry.id = entry.domain + entry.audience + entry.client_id;

			return entry;
		})
		.filter((a) => a !== null);

	if (entries.length !== 0 || auth0.canUseAuth0() === false) {
		return entries;
	}

	const config = auth0.getConfig();
	if (config.domain.length === 0 || config.audience.length === 0 || config.client_id.length === 0) {
		return entries;
	}

	return [
		{
			type: 'auth0',
			id: config.domain + config.audience + config.client_id,
			domain: config.domain,
			audience: config.audience,
			client_id: config.client_id,
		},
	];
}

export default function Login(props) {
	const [$auths] = React.useState(initAuths(props.auths));
	const [$canUseAuth0] = React.useState(auth0.canUseAuth0());
	const [$ready, setReady] = React.useState(false);
	const [$loginCheck, setLoginCheck] = React.useState(false);

	React.useEffect(() => {
		(async () => {
			if ($canUseAuth0) {
				auth0.init();
			}

			setReady(true);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Sign-in with no screen in front of it. Either this browser already holds
	// an Auth0 session, in which case the token exchange with Core is all that
	// is left, or it does not and we hand off to Auth0 immediately. Called once
	// from the effect below; the manual Continue button is the same function,
	// so a failed attempt shows that instead of retrying in a loop.
	async function signIn() {
		setLoginCheck(true);

		if ((await auth0.isAuthenticated()) === true) {
			await props.onAuth0();
			setLoginCheck(false);

			return;
		}

		const redirect = await auth0.login({
			address: props.address,
			hash: window.location.hash.substring(1),
		});

		if (redirect === false) {
			setLoginCheck(false);
		}
	}

	React.useEffect(() => {
		if ($ready === false || $canUseAuth0 === false || hasAuthType($auths, 'auth0') === false) {
			return;
		}

		signIn();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [$ready]);

	return (
		<Paper xs={11} sm={10} md={6}>
			<Grid container spacing={3} padding={{ xs: 1, sm: 5 }}>
				<Grid item xs={12} align="center">
					{$canUseAuth0 === false ? (
						<Typography>
							<Trans>Auth0 is currently not available because this interface is loaded from an insecure origin.</Trans>
						</Typography>
					) : hasAuthType($auths, 'auth0') === false ? (
						<Typography>
							<Trans>There's no login method available.</Trans>
						</Typography>
					) : (
						<React.Fragment>
							<Typography>
								<Trans>Signing you in with Auth0 ...</Trans>
							</Typography>
							<Button
								variant="outlined"
								color="primary"
								fullWidth
								size="large"
								type="submit"
								name="login_auth0"
								onClick={signIn}
								style={{ marginTop: '1.5rem' }}
							>
								<Trans>Continue</Trans>
							</Button>
						</React.Fragment>
					)}
				</Grid>
			</Grid>
			<Backdrop open={$loginCheck}>
				<CircularProgress color="inherit" />
			</Backdrop>
		</Paper>
	);
}

Login.defaultProps = {
	address: '',
	auths: [],
	hasService: false,
	onLogin: function (username, password) {},
	onAuth0: function () {},
};
